import type { RecommendationItem } from "@/types/api";
import type { CommercialDigestBooking } from "@/types/feed";
import { formatUsd } from "@/types/booking";

/**
 * Parsed view of an LCL first-book opportunity's `extra_data` payload, emitted
 * by the LCL first-book engine (`tideway-core::engine::lcl_first_book`). The
 * engine prices the cheapest matching LCL (W/M — weight-or-measure) lane tariff
 * per booking. When the booking's cargo dimensions are unknown it falls back to
 * quote mode (`quote_per_wm`), surfacing only the per-W/M unit price; otherwise
 * it computes a full shipment total (`wm_total`).
 */
export interface LclFirstBookQuote {
  /** `quote_per_wm` (cargo unknown) | `wm_total` (full shipment total). */
  pricingMode: "quote_per_wm" | "wm_total";
  /** Per-W/M-unit price (base FRT + per-revenue-unit surcharges). */
  frtPerWm: number;
  /** Flat per-shipment charges (BL / Shipment basis), added once. */
  flatPerShipment: number;
  /** Currency of all amounts. */
  currency: string;
  /** Chargeable revenue tons for the booking's cargo, when known. */
  chargeableWm: number | null;
  totalCbm: number | null;
  totalWeightKg: number | null;
  /** Full shipment total when cargo is known; `null` in quote mode. */
  quoteTotal: number | null;
  /** Freight basis label (defaults to "W/M"). */
  freightBasis: string;
}

function asNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

/** Whether a recommendation is an LCL first-book opportunity. */
export function isLclFirstBookRec(
  rec: Pick<
    RecommendationItem,
    "opportunity_kind" | "is_first_book" | "extra_data"
  >,
): boolean {
  const d = rec.extra_data;
  if (!d) return false;
  const kind = d.kind;
  const loadType = d.load_type;
  const pricingMode = d.pricing_mode;
  return (
    (rec.is_first_book === true || kind === "first_book") &&
    loadType === "lcl" &&
    (pricingMode === "quote_per_wm" || pricingMode === "wm_total")
  );
}

/**
 * Parse an LCL first-book recommendation's `extra_data` into a typed quote.
 * Returns `null` when the row is not an LCL first-book opportunity or the
 * payload is missing the per-W/M price.
 */
export function parseLclFirstBookQuote(
  rec: Pick<
    RecommendationItem,
    "opportunity_kind" | "is_first_book" | "extra_data"
  >,
): LclFirstBookQuote | null {
  if (!isLclFirstBookRec(rec)) return null;
  const d = rec.extra_data;
  if (!d) return null;
  const frtPerWm = asNumber(d.frt_per_wm);
  if (frtPerWm == null) return null;
  const pricingMode = d.pricing_mode === "wm_total" ? "wm_total" : "quote_per_wm";
  return {
    pricingMode,
    frtPerWm,
    flatPerShipment: asNumber(d.flat_per_shipment) ?? 0,
    currency: asString(d.currency) ?? "USD",
    chargeableWm: asNumber(d.chargeable_wm),
    totalCbm: asNumber(d.total_cbm),
    totalWeightKg: asNumber(d.total_weight_kg),
    quoteTotal: asNumber(d.quote_total),
    freightBasis: asString(d.freight_basis) ?? "W/M",
  };
}

function formatPerWm(frtPerWm: number, freightBasis: string): string {
  return `${formatUsd(frtPerWm)} / ${freightBasis}`;
}

function lclFieldsFromDigestBooking(
  booking: Pick<
    CommercialDigestBooking,
    "lcl_frt_per_wm" | "lcl_freight_basis" | "lcl_pricing_mode"
  >,
): Pick<LclFirstBookQuote, "pricingMode" | "frtPerWm" | "freightBasis"> | null {
  const frtPerWm = booking.lcl_frt_per_wm;
  if (frtPerWm == null || !Number.isFinite(frtPerWm)) return null;
  const pricingMode =
    booking.lcl_pricing_mode === "wm_total" ? "wm_total" : "quote_per_wm";
  return {
    pricingMode,
    frtPerWm,
    freightBasis: booking.lcl_freight_basis?.trim() || "W/M",
  };
}

/**
 * Display amount for a first-book digest / feed row — full total when known,
 * otherwise the per-W/M tariff (never a misleading `$0`).
 */
export function formatDigestFirstBookAmount(
  booking: Pick<
    CommercialDigestBooking,
    "first_book_price" | "lcl_frt_per_wm" | "lcl_freight_basis" | "lcl_pricing_mode"
  >,
  rec?: Pick<
    RecommendationItem,
    "opportunity_kind" | "is_first_book" | "extra_data" | "new_price"
  > | null,
): string {
  const fromRec = rec ? parseLclFirstBookQuote(rec) : null;
  if (fromRec) {
    if (fromRec.pricingMode === "quote_per_wm") {
      return formatPerWm(fromRec.frtPerWm, fromRec.freightBasis);
    }
    if (fromRec.quoteTotal != null) {
      return formatUsd(fromRec.quoteTotal);
    }
  }

  const fromPayload = lclFieldsFromDigestBooking(booking);
  if (fromPayload?.pricingMode === "quote_per_wm") {
    return formatPerWm(fromPayload.frtPerWm, fromPayload.freightBasis);
  }

  if (booking.first_book_price != null && booking.first_book_price > 0) {
    return formatUsd(booking.first_book_price);
  }
  if (fromPayload && fromPayload.pricingMode === "wm_total") {
    return formatPerWm(fromPayload.frtPerWm, fromPayload.freightBasis);
  }
  if (rec?.new_price != null && rec.new_price > 0) {
    return formatUsd(rec.new_price);
  }
  return formatUsd(booking.first_book_price ?? 0);
}
